"use client";

import createClient, { type Middleware } from "openapi-fetch";

import { URL_BACKEND } from "../lib/constants/urls/urlBackend";
import type { paths } from "./generated/schema";
import type { GetRequestHeadersProps } from "./utils/getRequestHeaders";
import { getRequestHeaders } from "./utils/getRequestHeaders";

export type CreateOpenApiClientProps = {
  headersValues?: GetRequestHeadersProps["values"];
  onTokenRefresh?: (token: string) => void;
};

export const createOpenApiClient = ({
  headersValues,
  onTokenRefresh,
}: CreateOpenApiClientProps = {}) => {
  const client = createClient<paths>({
    baseUrl: URL_BACKEND,
  });

  // Middleware to add auth headers
  const authMiddleware: Middleware = {
    async onRequest({ request }) {
      const headers = await getRequestHeaders({
        requestType: "authenticated",
        values: headersValues,
      });

      Object.entries(headers).forEach(([key, value]) => {
        if (value) request.headers.set(key, value);
      });

      return request;
    },
    async onResponse({ response }) {
      // Handle token refresh from response header
      if (response.headers.has("x-refreshed-token") && onTokenRefresh) {
        try {
          const newToken = JSON.parse(
            response.headers.get("x-refreshed-token") ?? "",
          ).access_token;
          if (newToken) {
            onTokenRefresh(newToken);
          }
        } catch {
          // Ignore parse errors
        }
      }
      return response;
    },
  };

  client.use(authMiddleware);

  return client;
};
